import { Suspense, lazy, useEffect } from "react";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import Page from "@/components/Page";
import Loading from "@/components/Loading";
import Layout from "./pages/Layout";
import "./App.css";

import { isMobile, isTablet, getUA } from "react-device-detect";

const isInWebView = () => {
  const userAgent = getUA.toLowerCase();

  const isAndroidWebView =
    /(android).*(wv|version\/\d[\d.]*\s(mobile|safari))/i.test(userAgent);
  const isIOSWebView = /(iphone|ipad|ipod).*applewebkit(?!.*safari)/i.test(
    userAgent
  );

  return isAndroidWebView || isIOSWebView || userAgent.includes("jiajiaclass");
};

// 集中定义路由配置
const routes = [
  {
    path: "/",
    element: <Layout />,
    children: [
      {
        index: true,
        title: "首页-蓝火箭AI",
        component: lazy(() => import("./pages/Home")),
      },
      { path: "work", component: lazy(() => import("./pages/Work")) },
      { path: "course", component: lazy(() => import("./pages/Course")) },
      {
        path: "pay",
        title: "支付-蓝火箭AI",
        component: lazy(() => import("./pages/Pay")),
      },
      {
        path: "paySuccess",
        title: "支付成功-蓝火箭AI",
        component: lazy(() => import("./pages/PaySuccess")),
      },
      {
        path: "myWork",
        title: "我的创作-蓝火箭AI",
        component: lazy(() => import("./pages/MyWork")),
      },
      {
        path: "vipShow",
        title: "订阅-蓝火箭AI",
        component: lazy(() => import("./pages/VipShow")),
      },
      {
        path: "allProblems",
        title: "题库-蓝火箭AI",
        component: lazy(() => import("./pages/AllProblems")),
      },
      {
        path: "collections",
        title: "题单-蓝火箭AI",
        component: lazy(() => import("./pages/Collections")),
      },
      {
        path: "problem/:problemId",
        title: "题目详情-蓝火箭AI",
        component: lazy(() => import("./pages/Problem")),
      },
      {
        path: "introduction",
        title: "快速了解-蓝火箭AI",
        component: lazy(() => import("./pages/Introduction")),
      },
      {
        path: "allCourses",
        title: "全部课程-蓝火箭AI",
        component: lazy(() => import("./pages/AllCourses")),
      },
      {
        path: "courseInfo",
        title: "课程详情-蓝火箭AI",
        component: lazy(() => import("./pages/CourseInfo")),
      },
      {
        path: "myCourses",
        title: "我的课程-蓝火箭AI",
        component: lazy(() => import("./pages/MyCourses")),
      },
      {
        path: "myOrders",
        title: "我的订单-蓝火箭AI",
        component: lazy(() => import("./pages/MyOrders")),
      },
      {
        path: "orderConfirm",
        title: "确认订单-蓝火箭AI",
        component: lazy(() => import("./pages/OrderConfirm")),
      },
      {
        path: "orderConfirm/camp",
        title: "确认订单-蓝火箭AI",
        component: lazy(() => import("./pages/OrderConfirmCamp")),
      },
      {
        path: "orderInfo",
        title: "订单详情-蓝火箭AI",
        component: lazy(() => import("./pages/OrderInfo")),
      },
      {
        path: "orderResult",
        title: "订单结果-蓝火箭AI",
        component: lazy(() => import("./pages/OrderResult")),
      },
      {
        path: "enter",
        title: "登录-蓝火箭AI",
        component: lazy(() => import("./pages/Enter")),
      },
      {
        path: "register",
        title: "注册-蓝火箭AI",
        component: lazy(() => import("./pages/Register")),
      },
      {
        path: "registerInfo",
        title: "注册信息-蓝火箭AI",
        component: lazy(() => import("./pages/RegisterInfo")),
      },
      {
        path: "passwordFind",
        title: "找回密码-蓝火箭AI",
        component: lazy(() => import("./pages/PasswordFind")),
      },
      {
        path: "passwordReset",
        title: "密码重置-蓝火箭AI",
        component: lazy(() => import("./pages/PasswordReset")),
      },
      {
        path: "personalInfo",
        title: "个人信息-蓝火箭AI",
        component: lazy(() => import("./pages/PersonalInfo")),
      },
      {
        path: "infoEdit",
        title: "个人信息编辑-蓝火箭AI",
        component: lazy(() => import("./pages/InfoEdit")),
      },
      {
        path: "audioCheck",
        title: "语音检测-蓝火箭AI",
        component: lazy(() => import("./pages/AudioCheck")),
      },
      {
        path: "downLoad",
        title: "下载软件-蓝火箭AI",
        component: lazy(() => import("./pages/DownLoad")),
      },
      {
        path: "shareWork",
        title: "应用-蓝火箭AI",
        component: lazy(() => import("./pages/ShareWork")),
      },
    ],
  },
  {
    path: "desktop",
    title: "蓝火箭桌面端-蓝火箭AI",
    component: lazy(() => import("./pages/BlueRocketDesktop")),
  },
  {
    path: "wap/shareWork",
    title: "分享作品-蓝火箭AI",
    component: lazy(() => import("./pages/wap/ShareWork")),
  },
  {
    path: "appDownload",
    title: "下载-蓝火箭AI",
    component: lazy(() => import("./pages/AppDownload")),
  },
  {
    path: "mobileCheck",
    title: "下载",
    component: lazy(() => import("./pages/MobileCheck")),
  },
];

// 渲染路由组件
const renderRoute = (route: any) => {
  const Component = route.component;
  return route.title ? (
    <Page title={route.title} component={Component} />
  ) : (
    <Component />
  );
};

// 递归生成路由
const generateRoutes = (routes: any[]) => {
  return routes.map((route) => (
    <Route
      key={route.path || "index"}
      path={route.path}
      index={route.index}
      element={route.children ? route.element : renderRoute(route)}
    >
      {route.children && generateRoutes(route.children)}
    </Route>
  ));
};

function App() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (
      (isMobile || isTablet) &&
      !isInWebView() &&
      !/shareWork|introduction/.test(location.pathname)
    ) {
      navigate("/mobileCheck");
    }
  }, [navigate]);

  return (
    <div className="App">
      <Suspense fallback={<Loading />}>
        <Routes>{generateRoutes(routes)}</Routes>
      </Suspense>
    </div>
  );
}

export default App;
